export interface AssistantOutputControlState {
  path: string;
  label: string;
  type: string;
  value: unknown;
  checked?: boolean;
  disabled?: boolean;
}

export interface AssistantChartState {
  context_key: string;
  name: string;
  label: string;
  type: string;
  narrative: string;
  digest?: unknown;
  view_state?: unknown;
}

export interface AssistantModelSyncState {
  model_name: string | null;
  draft_id: string | null;
  model_type: string | null;
  status: string | null;
  has_stored_changes: boolean;
  total_changes: number;
  current_input: unknown;
  output_text: string;
  output_controls: AssistantOutputControlState[];
  charts: AssistantChartState[];
}

export interface AssistantSyncChanges {
  inputs_changed: string[];
  output_controls_changed: string[];
  output_sections_changed: string[];
  charts_changed: string[];
  summary_changed: boolean;
}

export interface AssistantSyncPayload {
  previous_fingerprint?: string;
  current_fingerprint: string;
  reason: string;
  current_state: AssistantModelSyncState;
  changes: AssistantSyncChanges;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.getPrototypeOf(value) === Object.prototype;
};

const normalizeValue = (value: unknown, seen: WeakSet<object>): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => normalizeValue(item, seen));
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value && typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);

    if (!isPlainObject(value)) {
      return String(value);
    }

    const entries = Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, nestedValue]) => [key, normalizeValue(nestedValue, seen)] as const);

    return Object.fromEntries(entries);
  }

  return value;
};

export const stableStringify = (value: unknown): string => {
  return JSON.stringify(normalizeValue(value, new WeakSet<object>()));
};

export const createAssistantModelStateFingerprint = (state: AssistantModelSyncState): string => {
  const serialized = stableStringify(state);
  let hash = 2166136261;

  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(16).padStart(8, '0');
};

const valuesEqual = (left: unknown, right: unknown): boolean => {
  return stableStringify(left) === stableStringify(right);
};

const diffValuePaths = (previous: unknown, current: unknown, path: string): string[] => {
  if (valuesEqual(previous, current)) {
    return [];
  }

  if (Array.isArray(previous) && Array.isArray(current)) {
    const maxLength = Math.max(previous.length, current.length);
    const nextPaths: string[] = [];

    for (let index = 0; index < maxLength; index += 1) {
      const nestedPath = `${path}[${index}]`;
      nextPaths.push(...diffValuePaths(previous[index], current[index], nestedPath));
    }

    return nextPaths.length > 0 ? nextPaths : [path];
  }

  if (isPlainObject(previous) && isPlainObject(current)) {
    const keys = Array.from(new Set([...Object.keys(previous), ...Object.keys(current)])).sort();
    const nextPaths = keys.flatMap(key => {
      const nestedPath = path ? `${path}.${key}` : key;
      return diffValuePaths(previous[key], current[key], nestedPath);
    });

    return nextPaths.length > 0 ? nextPaths : [path];
  }

  return path ? [path] : [];
};

const buildInputChangeList = (
  previousInput: AssistantModelSyncState['current_input'],
  currentInput: AssistantModelSyncState['current_input'],
): string[] => {
  if (valuesEqual(previousInput, currentInput)) {
    return [];
  }

  if (isPlainObject(previousInput) && isPlainObject(currentInput)) {
    const keys = Array.from(new Set([...Object.keys(previousInput), ...Object.keys(currentInput)])).sort();
    return keys.flatMap(key => diffValuePaths(previousInput[key], currentInput[key], key));
  }

  return ['current_input'];
};

const buildOutputControlChangeList = (
  previousControls: AssistantOutputControlState[],
  currentControls: AssistantOutputControlState[],
): string[] => {
  const previousByPath = new Map(previousControls.map(control => [control.path, control]));
  const currentByPath = new Map(currentControls.map(control => [control.path, control]));

  return Array.from(new Set([...previousByPath.keys(), ...currentByPath.keys()]))
    .sort()
    .filter(path => {
      const previousControl = previousByPath.get(path);
      const currentControl = currentByPath.get(path);

      if (!previousControl || !currentControl) {
        return true;
      }

      return !valuesEqual(previousControl, currentControl);
    });
};

const buildOutputSections = (outputText: string): Record<string, string> => {
  const sections: Record<string, string> = {};
  const counters = new Map<string, number>();
  const lines = outputText.split('\n');
  let currentKey: string | null = null;

  lines.forEach(rawLine => {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      return;
    }

    const isTopLevel = rawLine.length > 0 && rawLine === rawLine.trimStart();
    if (isTopLevel) {
      const labelBase = line.includes(':') ? line.split(':', 1)[0].trim() : line.trim();
      const occurrence = (counters.get(labelBase) ?? 0) + 1;
      counters.set(labelBase, occurrence);
      currentKey = occurrence === 1 ? labelBase : `${labelBase} (${occurrence})`;
      sections[currentKey] = line;
      return;
    }

    if (currentKey) {
      sections[currentKey] = `${sections[currentKey]}\n${line}`;
    }
  });

  return sections;
};

const buildOutputSectionChangeList = (previousOutputText: string, currentOutputText: string): string[] => {
  const previousSections = buildOutputSections(previousOutputText);
  const currentSections = buildOutputSections(currentOutputText);
  const sectionKeys = Array.from(new Set([...Object.keys(previousSections), ...Object.keys(currentSections)])).sort();

  return sectionKeys.filter(key => !valuesEqual(previousSections[key], currentSections[key]));
};

const buildChartChangeList = (
  previousCharts: AssistantChartState[],
  currentCharts: AssistantChartState[],
): string[] => {
  const previousByKey = new Map(previousCharts.map(chart => [chart.context_key, chart]));
  const currentByKey = new Map(currentCharts.map(chart => [chart.context_key, chart]));

  return Array.from(new Set([...previousByKey.keys(), ...currentByKey.keys()]))
    .sort()
    .filter(key => {
      const previousChart = previousByKey.get(key);
      const currentChart = currentByKey.get(key);

      if (!previousChart || !currentChart) {
        return true;
      }

      return !valuesEqual(previousChart, currentChart);
    })
    .map(key => currentByKey.get(key)?.label || previousByKey.get(key)?.label || key);
};

export const buildAssistantSyncChanges = (
  previousState: AssistantModelSyncState,
  currentState: AssistantModelSyncState,
): AssistantSyncChanges => {
  return {
    inputs_changed: buildInputChangeList(previousState.current_input, currentState.current_input),
    output_controls_changed: buildOutputControlChangeList(previousState.output_controls, currentState.output_controls),
    output_sections_changed: buildOutputSectionChangeList(previousState.output_text, currentState.output_text),
    charts_changed: buildChartChangeList(previousState.charts, currentState.charts),
    summary_changed: previousState.output_text !== currentState.output_text,
  };
};

export const getAssistantSyncReason = (
  changes: AssistantSyncChanges,
  previousState?: AssistantModelSyncState,
): string => {
  if (!previousState) {
    return 'session resumed with current model state';
  }

  if (changes.summary_changed || changes.output_sections_changed.length > 0) {
    return 'outputs regenerated';
  }

  if (changes.charts_changed.length > 0) {
    return 'chart context changed';
  }

  if (changes.output_controls_changed.length > 0 && changes.inputs_changed.length === 0) {
    return 'output view controls changed';
  }

  if (changes.inputs_changed.length > 0) {
    return 'inputs changed';
  }

  return 'model state changed';
};
